import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ZillHubLogoBlue from "../../assets/ZillHubLogoBlue_noBG.png";
import ZillHubLogoBlack from "../../assets/ZillHubLogoBlack_noBG.png";
import { WaitlistSignup } from "../../components/WaitlistSignup";
import { 
  Brain, Shield, Sparkles, Target, TrendingUp, Award, Zap,
  BarChart3, ArrowRight, Heart, Users, CheckCircle, XCircle,
  Trophy, Flame, UserCircle, BookOpen, Headphones, Gift, BarChart
} from "lucide-react"; 

const Home: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = prev || "";
    };
  }, []);

  const features = [
    {
      icon: Sparkles,
      title: "ZillAi Coach",
      desc: "Talk it through anytime. Get a steady next step, encouragement, and personalized lesson recommendations based on your goals and what you're facing.",
      gradient: "from-purple-600 to-pink-600"
    },
    {
      icon: Trophy,
      title: "Daily Challenges",
      desc: "Small daily practice that builds real strength. Earn points, keep streaks, and sharpen your judgment with quick games and challenges.",
      gradient: "from-amber-600 to-orange-600"
    },
    {
      icon: BarChart3,
      title: "Track Progress",
      desc: "See what's getting stronger. Follow your momentum with simple charts, badges, milestones, and weekly snapshots of your growth.",
      gradient: "from-blue-600 to-cyan-600"
    },
    {
      icon: BookOpen,
      title: "Progressive Learning",
      desc: "800+ interactive lessons across 24 life skill areas, from beginner to advanced, unlocking as you build confidence and consistency.",
      gradient: "from-green-600 to-emerald-600"
    }
  ];

  const stats = [
    { 
      number: "24", 
      label: "Core Life Skills", 
      desc: "Skills for emotions, relationships, decisions, and daily life",
      icon: UserCircle,
      gradient: "from-indigo-500 to-purple-500"
    },
    { 
      number: "800+", 
      label: "Interactive Lessons", 
      desc: "Short, practical, and built for real situations",
      icon: BookOpen,
      gradient: "from-purple-500 to-pink-500"
    },
    { 
      number: "24/7", 
      label: "AI Coach Support", 
      desc: "Guidance anytime you need a steady next step",
      icon: Headphones,
      gradient: "from-pink-500 to-rose-500"
    },
    { 
      label: "Learning Tracks", 
      desc: "Curated step-by-step journeys that build real-life skills with purpose",
      icon: Brain,
      gradient: "from-violet-500 to-purple-500"
    },
    { 
      label: "Growth Tracking", 
      desc: "See your progress with personalized monthly insights",
      icon: BarChart,
      gradient: "from-blue-500 to-cyan-500"
    },
    { 
      number: "Free", 
      label: "To Get Started", 
      desc: "Begin today, no pressure",
      icon: Gift,
      gradient: "from-emerald-500 to-teal-500"
    },
  ];

  return (
    <div className="bg-gradient-to-b from-slate-50 via-white to-slate-50 min-h-screen">
      {/* Skip navigation - ADA/WCAG 2.1 AA */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-indigo-600 focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus:shadow-lg focus:ring-2 focus:ring-indigo-500"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200/50 shadow-sm">
        <nav className="flex items-center justify-between p-4 lg:px-8 max-w-7xl mx-auto">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5">
              <img src={ZillHubLogoBlue} alt="ZillHub" width="90" height="40" className="h-10 w-auto" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2.5 text-slate-700 hover:text-slate-900"
            >
              <span className="sr-only">Open menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex lg:gap-x-8">
            <a href="#features" className="text-sm font-medium text-slate-700 hover:text-indigo-600 transition">Features</a>
            <a href="#domains" className="text-sm font-medium text-slate-700 hover:text-indigo-600 transition">Life Skills</a>
            <a href="#how-it-works" className="text-sm font-medium text-slate-700 hover:text-indigo-600 transition">How It Works</a>
            <a href="#pricing" className="text-sm font-medium text-slate-700 hover:text-indigo-600 transition">Pricing</a>
          </div>

          <div className="hidden lg:flex lg:flex-1 lg:justify-end gap-3">
            <Link
              to="/login"
              className="px-5 py-2.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition"
            >
              Log in
            </Link>
            <Link
              to="/login"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/30 transition"
            >
              Start Free
            </Link>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white p-6 shadow-2xl relative z-10">
              <div className="flex items-center justify-between mb-6">
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <img src={ZillHubLogoBlue} alt="ZillHub" width="72" height="32" className="h-8 w-auto" />
                </Link>
                <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2 text-slate-500 hover:text-slate-700">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                <a href="#features" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-base font-semibold text-slate-900 hover:bg-slate-50 rounded-lg transition">Features</a>
                <a href="#domains" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-base font-semibold text-slate-900 hover:bg-slate-50 rounded-lg transition">Life Skills</a>
                <a href="#how-it-works" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-base font-semibold text-slate-900 hover:bg-slate-50 rounded-lg transition">How It Works</a>
                <a href="#pricing" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-base font-semibold text-slate-900 hover:bg-slate-50 rounded-lg transition">Pricing</a>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-center text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg mt-4 shadow-lg">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <main id="main-content" className="pt-20">
        <section className="relative overflow-hidden">
          {/* Gradient orbs */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-1/2 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
          
          <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 sm:pt-24 sm:pb-32">
            <div className="text-center max-w-4xl mx-auto">
              {/* Logo */}
              <div className="mb-8 flex justify-center">
                <img src={ZillHubLogoBlue} alt="ZillHub" width="180" height="80" className="h-20 sm:h-24 w-auto object-contain drop-shadow-lg" />
              </div>

              {/* Badge */}
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full border border-indigo-200">
                <Sparkles className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-900">Skills for Real Life</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Show Up Strong
                <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  When It Matters
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl sm:text-2xl text-slate-600 mb-10 max-w-4xl mx-auto leading-relaxed">
                <span className="font-bold text-indigo-600">Resilience</span> isn't pretending you're fine. It's noticing what you feel, choosing what you do next, and living your values through stress, conflict, pressure, and change. It helps you stay on your path when life pulls you off course, get back up when you're knocked down, and become more of the person you want to be, one practical lesson at a time.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/login"
                  className="group px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full hover:from-indigo-700 hover:to-purple-700 shadow-xl shadow-indigo-500/30 transition-all hover:scale-105 flex items-center gap-2"
                >
                  Start Your Free Training
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#how-it-works"
                  className="px-8 py-4 text-lg font-bold text-indigo-600 border-2 border-indigo-600 rounded-full hover:bg-indigo-50 transition-all flex items-center gap-2"
                >
                  See How It Works
                  <Zap className="h-5 w-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {stats.map((stat, idx) => (
                  <div 
                    key={idx} 
                    className="group relative bg-white rounded-3xl p-8 border-2 border-slate-200 hover:border-transparent shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                  >
                    {/* Gradient background on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.gradient} group-hover:bg-white/20 mb-5 shadow-lg transition-all`}>
                        <stat.icon className="h-8 w-8 text-white" />
                      </div>
                      
                      {/* Number (if exists) */}
                      {stat.number && (
                        <div className="text-5xl font-black text-slate-900 group-hover:text-white mb-2 transition-colors">
                          {stat.number}
                        </div>
                      )}
                      
                      {/* Label */}
                      <p className="text-xl font-bold text-slate-900 group-hover:text-white mb-2 transition-colors">
                        {stat.label}
                      </p>
                      
                      {/* Description */}
                      <p className="text-sm text-slate-600 group-hover:text-white/90 leading-relaxed transition-colors">
                        {stat.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hero Waitlist Section */}
        <section className="relative py-24 overflow-hidden">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6bTAtMjBjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6TTIwIDM0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wLTIwYzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0tMTYgMjBjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6bTAtMjBjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10" />
          
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-6">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-semibold text-white">Coming Soon</span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-6">
              Be the first to know when we launch
            </h2>
            <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
              Join our exclusive waitlist and get early access to ZillHub plus special launch bonuses.
            </p>
            
            <div className="max-w-md mx-auto">
              <WaitlistSignup variant="compact" />
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full border border-purple-200 mb-4">
                <Zap className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">Powerful Features</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
                Everything you need to show up strong
                <span className="block text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text">
                  when it matters
                </span>
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto">
                Build resilience through guided lessons, daily practice, and supportive coaching. You'll learn to notice what you feel, choose what you do next, and stay on your path through stress, conflict, pressure, and change.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, idx) => (
                <div key={idx} className="group relative bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 border border-slate-200 hover:border-slate-300 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-r ${feature.gradient} mb-5 shadow-lg`}>
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 text-lg leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Three Dimensions Overview */}
        <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-200 mb-4">
                <Target className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Three Dimensions of Life</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
                Skills Organized Around
                <span className="block text-transparent bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text">
                  How You Experience Life
                </span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our curriculum is built around three dimensions. These are the areas where life challenges you most.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 mb-6 shadow-lg">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Individual & Psychological</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your inner world: understanding yourself, managing emotions, building confidence, staying resilient.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:border-purple-300 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 mb-6 shadow-lg">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Social & Environmental</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your relationships and surroundings: navigating friendships, family dynamics, social pressures, and community.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-500 mb-6 shadow-lg">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Behavioral & Physical</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your actions and choices: making good decisions, setting goals, handling challenges, taking care of yourself.
                </p>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/how-it-works" 
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
              >
                <span>See Full Curriculum Structure</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Parent Safety & Monitoring Section */}
        <section className="py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 right-10 w-80 h-80 bg-emerald-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-emerald-200 mb-4 shadow-lg">
                <Shield className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-900">Built for Families, Made for Everyone</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
                Safety & Transparency
                <span className="block text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text">
                  For All Ages
                </span>
              </h2>
              <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
                ZillHub is designed for people of all ages - children, teens, young adults, and beyond - to build essential resilience skills. For younger users (under 15), we provide additional parental monitoring and automatic safety alerts to ensure a safe, supported learning environment.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 mb-6 shadow-lg">
                  <BarChart3 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Weekly Progress Reports</h3>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Get detailed updates on your child's learning achievements, completed lessons, skill development, and resilience growth.
                </p>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-teal-100">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-500 mb-6 shadow-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">AI Safety Monitoring</h3>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Our AI monitors conversations, journal entries, and check-ins for concerning content. Immediate alerts for high-risk situations like self-harm or distress.
                </p>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-cyan-100">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 mb-6 shadow-lg">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Privacy-Preserving Approach</h3>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Alerts provide categories and guidance without full transcripts, balancing safety monitoring with your child's privacy and dignity.
                </p>
              </div>
            </div>

            <div className="mt-12 bg-white/90 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-emerald-500 shadow-xl">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-100">
                    <Sparkles className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">Parental Consent Required for Ages 5-14</h4>
                  <p className="text-slate-700 leading-relaxed">
                    We comply with COPPA and require verified parental approval before children under 15 can use ZillHub. Parents receive immediate notification of account creation and must approve before access is granted.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coach Personas Section */}
        <section className="py-24 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-72 h-72 bg-purple-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-pink-400 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-orange-400 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-purple-200 mb-4 shadow-lg">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">Personalized Coaching</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
                Your Spirit Animal Coach
                <span className="block text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text">
                  Guides Your Journey
                </span>
              </h2>
              <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
                Choose a coaching style that resonates with you. Each spirit animal brings a unique approach to help you build resilience and grow stronger.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Leo the Lion */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-amber-400">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FLeoLion.png?alt=media&token=bc2eb94c-36d6-4e73-a506-0a332e2542c7" 
                      alt="Leo the Lion"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Lionel</h3>
                  <p className="text-amber-600 font-semibold text-center mb-4">Courageous • Strong • Confident</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    Bold and direct, Lionel leads with confidence and helps you discover your inner strength. Perfect for those who want clear direction and empowering challenges.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">Leadership</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">Direct</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">Empowering</span>
                  </div>
                </div>
              </div>

              {/* Berty Stratton */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-blue-400">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FBerty.png?alt=media&token=a4e0446a-4984-428a-8b6e-ded087cd9378" 
                      alt="Berty Stratton"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Berty Stratton</h3>
                  <p className="text-blue-600 font-semibold text-center mb-4">Wise • Strategic • Thoughtful</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    Wise and strategic, Berty helps you see the bigger picture and make thoughtful decisions. Ideal for those who appreciate depth and careful planning.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Wisdom</span>
                    <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">Strategic</span>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">Patient</span>
                  </div>
                </div>
              </div>

              {/* Owlbus */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-indigo-400">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FOwl.png?alt=media&token=0cb0e768-7a2b-442a-a4da-fab35a127cac" 
                      alt="Owlbus"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<span class="text-6xl">🦉</span>';
                      }}
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Owlbus</h3>
                  <p className="text-indigo-600 font-semibold text-center mb-4">Wise • Observant • Insightful</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    Wise and observant, Owlbus sees through darkness and helps you discover clarity in confusion. Perfect for those seeking deep insight and understanding.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">Wisdom</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Insight</span>
                    <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">Clarity</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-full text-white font-bold text-lg shadow-2xl hover:shadow-3xl transition-all hover:scale-105 cursor-pointer">
                <Sparkles className="h-6 w-6" />
                <span>Choose Your Coach & Start Learning</span>
                <ArrowRight className="h-6 w-6" />
              </div>
              <p className="mt-4 text-slate-600">
                More coaching styles available for pro members
              </p>
            </div>
          </div>
        </section>

        {/* People Coaches Section */}
        <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 opacity-20" aria-hidden="true">
            <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-teal-400 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200 mb-4 shadow-lg">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Expert Mentors</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
                Your Personal Coach
                <span className="block text-transparent bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text">
                  Empowers Your Growth
                </span>
              </h2>
              <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
                Connect with experienced coaches who bring real-world wisdom and proven strategies. Each mentor offers a unique perspective to help you navigate life's challenges with confidence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Ektor */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-emerald-400">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FEktor.png?alt=media&token=e3b9dcc2-1ead-4aed-b689-37fa0bcc4d90" 
                      alt="Ektor"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Ektor</h3>
                  <p className="text-emerald-700 font-semibold text-center mb-4">Stoic • Principled • Grounded</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    A Stoic sage who builds resilience through virtue, reason, and mastery of what is within your control. Perfect for those seeking calm clarity and inner strength.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">Virtue</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">Stoic</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Contemplative</span>
                  </div>
                </div>
              </div>

              {/* Kimberly */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-pink-400">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FKimberly.png?alt=media&token=87eca646-0ca3-4e8b-ad1a-f6af8431a5b3" 
                      alt="Kimberly"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Kimberly</h3>
                  <p className="text-pink-700 font-semibold text-center mb-4">Caring • Nurturing • Supportive</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    A caring coach who builds resilience through nurturing support, encouragement, and gentle guidance. Perfect for those who value warmth and compassion.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">Caring</span>
                    <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-medium">Nurturing</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Supportive</span>
                  </div>
                </div>
              </div>

              {/* Socrates */}

              {/* Socrates */}
              <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-3 border-4 border-transparent hover:border-amber-400">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="relative z-10">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden shadow-2xl group-hover:scale-110 transition-transform bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    <img 
                      src="https://firebasestorage.googleapis.com/v0/b/drop-the-pitch.firebasestorage.app/o/CoachAvatars%2FSocrates.png?alt=media&token=b3b2b116-f6dc-4cdb-aee0-0e1eb74df48f" 
                      alt="Socrates"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">Socrates</h3>
                  <p className="text-amber-700 font-semibold text-center mb-4">Philosophical • Questioning • Insightful</p>
                  
                  <p className="text-slate-700 text-center leading-relaxed mb-6">
                    A Greek philosopher who builds resilience through questioning, self-examination, and the pursuit of wisdom. Perfect for those who seek truth and understanding.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">Wisdom</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">Questioning</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">Philosophical</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 rounded-full text-white font-bold text-lg shadow-2xl hover:shadow-3xl transition-all hover:scale-105 cursor-pointer">
                <Users className="h-6 w-6" />
                <span>Meet Your Personal Coach</span>
                <ArrowRight className="h-6 w-6" />
              </div>
              <p className="mt-4 text-slate-700">
                All coaches available for pro members
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full border border-teal-200 mb-4">
                <Flame className="h-4 w-4 text-teal-600" />
                <span className="text-sm font-semibold text-teal-900">Simple & Effective</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
                How ZillHub
                <span className="block text-transparent bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text">
                  helps you grow stronger
                </span>
              </h2>
            </div>

            <div className="relative max-w-5xl mx-auto">
              <div className="grid md:grid-cols-3 gap-12 md:gap-8">
                {[
                  { step: "01", icon: Target, title: "Choose Your Path", desc: "Start with what you need most, or follow personalized recommendations based on your goals and reflections." },
                  { step: "02", icon: Trophy, title: "Learn and Practice", desc: "Complete short lessons and interactive activities that build real life skills you can use the same day." },
                  { step: "03", icon: TrendingUp, title: "Track Your Growth", desc: "See your progress over time, earn milestones, and get supportive coaching to help you stay on your path." }
                ].map((item, idx) => (
                  <div key={idx} className="relative flex flex-col items-center">
                    <div className="relative z-10 text-center">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-2xl font-black mb-6 shadow-xl">
                        {item.step}
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-600 mb-5 shadow-lg">
                        <item.icon className="h-8 w-8 text-white" />
                      </div>
                      <p className="text-slate-600 text-lg leading-relaxed">{item.desc}</p>
                    </div>
                    {idx < 2 && (
                      <ArrowRight className="hidden md:block absolute top-10 -right-6 lg:-right-4 h-8 w-8 text-teal-300" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Waitlist Before Pricing */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <WaitlistSignup />
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full border border-amber-200 mb-4">
                <Award className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-900">Simple Pricing</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
                Start free,
                <span className="block text-transparent bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text">
                  upgrade when ready
                </span>
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Get started with core features free forever. Upgrade for more power.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {/* Free Tier */}
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-200 shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-5xl font-extrabold text-slate-900">$0</span>
                    <span className="text-slate-500 font-medium">/forever</span>
                  </div>
                  <p className="text-slate-600">Perfect to get started and build the habit</p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Standard audio lessons (Basic TTS)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">3 custom lessons per month (standard audio)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Daily resilience check-ins</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Daily challenge activity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Unlimited personal journal entries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">20 ZillAi chats per month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="h-6 w-6 text-slate-300 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-500">Premium (Neural2) audio lessons</span>
                  </li>
                </ul>

                <Link
                  to="/login"
                  className="block w-full text-center px-6 py-4 text-lg font-bold text-indigo-600 border-2 border-indigo-600 rounded-full hover:bg-indigo-50 transition"
                >
                  Start Free
                </Link>
              </div>

              {/* Pro Tier */}
              <div className="relative bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 shadow-2xl border-2 border-indigo-500">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                    Resilience Booster
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-5xl font-extrabold text-white">$9</span>
                    <span className="text-indigo-200 font-medium">/month</span>
                  </div>
                  <p className="text-indigo-100">Unlock your full potential</p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">All Free features</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">20 custom lessons per month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Premium audio (Neural2) on 10 lessons / month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Standard audio on remaining 10 lessons / month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">150 ZillAi chats per month</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">AI recommended lessons</span>
                  </li>
                </ul>

                <Link
                  to="/login"
                  className="block w-full text-center px-6 py-4 text-lg font-bold text-indigo-600 bg-white rounded-full hover:bg-gray-50 transition shadow-xl"
                >
                  Go Pro
                </Link>
              </div>

              {/* Pro+ Tier */}
              <div className="bg-white rounded-3xl p-8 border-2 border-gradient-to-r from-pink-500 to-purple-500 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-50 to-purple-50 opacity-50" />
                <div className="relative z-10">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Pro+</h3>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-5xl font-extrabold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">$18</span>
                      <span className="text-slate-500 font-medium">/month</span>
                    </div>
                    <p className="text-slate-600">Maximum growth, fewer limits</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">All Pro features</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">40 custom lessons per month</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">All lessons premium audio (Neural2)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">300 ZillAi chats per month</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">Early access to new features</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 font-medium">Dedicated lesson media player (coming soon)</span>
                    </li>
                  </ul>

                  <Link
                    to="/login"
                    className="block w-full text-center px-6 py-4 text-lg font-bold text-white bg-gradient-to-r from-pink-600 to-purple-600 rounded-full hover:from-pink-700 hover:to-purple-700 transition shadow-xl"
                  >
                    Go Pro+
                  </Link>
                </div>
              </div>

              {/* Family Tier */}
              <div className="relative bg-gradient-to-br from-green-600 to-teal-600 rounded-3xl p-8 shadow-2xl border-2 border-green-500">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                    Family Plan
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Family</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-extrabold text-white">$14</span>
                    <span className="text-green-200 font-medium">/month</span>
                  </div>
                  <p className="text-green-100 text-sm">Includes parent + 1 child</p>
                  <p className="text-green-200 text-sm">+$5/mo per additional child</p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Parent gets full Pro access</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Each child gets Pro access</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Assign lessons to children</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Track children's progress</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">Base: $14/mo (parent + 1 child)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-6 w-6 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">$5/mo per additional child</span>
                  </li>
                </ul>

                <Link
                  to="/login"
                  className="block w-full text-center px-6 py-4 text-lg font-bold text-green-700 bg-white rounded-full hover:bg-gray-50 transition shadow-xl"
                >
                  Go Family
                </Link>
              </div>
            </div>
          </div>

          {/* Compare Plans CTA */}
          <div className="text-center mt-12">
            <Link
              to="/compare-plans"
              className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-full hover:border-indigo-400 hover:text-indigo-600 shadow-sm hover:shadow-md transition-all"
            >
              Compare all features in detail
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Sparkles className="h-16 w-16 text-white mx-auto mb-6 drop-shadow-lg" />
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-6">
              Ready to build unshakeable resilience?
            </h2>
            <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
              Join ZillHub to begin mastering life skills and becoming unstoppable.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-10 py-5 text-xl font-bold text-indigo-600 bg-white rounded-full hover:bg-gray-50 shadow-2xl transition-all hover:scale-105"
            >
              Start Your Free Training
              <ArrowRight className="h-6 w-6" />
            </Link>
            <p className="mt-6 text-indigo-200">No credit card required • Start earning points in 60 seconds</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src={ZillHubLogoBlack} alt="ZillHub" className="h-12 w-auto opacity-80" />
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms-of-service" className="text-sm text-slate-400 hover:text-white transition">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="text-sm text-slate-400 hover:text-white transition">
                Privacy Policy
              </Link>
            </div>
            <div className="text-sm text-slate-400">
              © 2025 ZillHub — All rights reserved
            </div>
          </div>
        </div>
      </footer>

      {/* Add animation keyframes */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}} />
    </div>
  );
};

export default Home;
